import { OrbitalElements } from './OrbitalElements';

export type BodyKind = 'star' | 'planet' | 'gasGiant' | 'moon' | 'asteroid' | 'station';

/** How a body's surface is generated and what the player can do there. */
export type SurfaceClass =
  | 'none' // gas giant or star: no landable surface
  | 'rocky'
  | 'terrestrial'
  | 'oceanic'
  | 'volcanic'
  | 'frozen'
  | 'desert'
  | 'jungle'
  | 'toxic';

export interface AtmosphereDef {
  /** Sea-level density, kg/m^3. Zero means vacuum. */
  surfaceDensity: number;
  /** Exponential scale height, metres. */
  scaleHeight: number;
  /** Sea-level pressure, pascals. */
  surfacePressure: number;
  /** Altitude above which aerodynamic forces are negligible, metres. */
  topAltitude: number;
  /** Mean molar mass, kg/mol. Drives speed of sound and scale height checks. */
  molarMass: number;
  /** Volume fractions by species; must be descriptive, not exhaustive. */
  composition: Record<string, number>;
  /** Whether the mix supports unprotected breathing. */
  breathable: boolean;
  /** Rayleigh scattering coefficients at sea level, per metre, RGB. */
  rayleigh: [number, number, number];
  /** Mie scattering coefficient at sea level, per metre. */
  mie: number;
  /** Mie phase asymmetry, -1..1. */
  mieAnisotropy: number;
  /** Base tint used for ground haze and horizon glow. */
  hazeColor: [number, number, number];
  /** Mean wind speed at low altitude, m/s. */
  windSpeed: number;
  /** Turbulence intensity, 0..1. */
  turbulence: number;
  /** Fraction of the sky covered by cloud, 0..1. Zero means a clear world. */
  cloudCoverage: number;
  /** Altitude of the main cloud deck, metres. */
  cloudAltitude: number;
  /** Cloud albedo tint. Water clouds are white; sulphuric ones are not. */
  cloudColor: [number, number, number];
}

export interface ThermalDef {
  /** Mean surface temperature at the equator, kelvin. */
  meanSurfaceTemperature: number;
  /** Equator-to-pole temperature drop, kelvin. */
  polarDelta: number;
  /** Day-night swing, kelvin. */
  diurnalRange: number;
  /** Lapse rate, kelvin per metre of altitude. */
  lapseRate: number;
}

export interface HazardDef {
  /** Ambient ionising radiation at the surface, sieverts/hour. */
  radiation: number;
  /** 0..1 likelihood weighting for severe weather events. */
  stormFrequency: number;
  /** 0..1 likelihood weighting for seismic and volcanic activity. */
  seismicActivity: number;
  /** Corrosive or reactive atmosphere damage rate, hull points/hour. */
  corrosion: number;
  /** Short descriptions surfaced in the survey UI. */
  notes: string[];
}

/** Inputs to the procedural terrain pipeline. Entirely data, no code. */
export interface TerrainProfile {
  /** Peak-to-trough relief budget, metres. */
  maxElevation: number;
  /** Datum offset for sea level as a fraction of `maxElevation`, -1..1. */
  seaLevelFraction: number;
  /** Target ocean coverage, 0..1. Used to bias the continental mask. */
  oceanCoverage: number;
  /** Characteristic continent wavelength, metres. */
  continentScale: number;
  /** Number of tectonic plates. Zero disables tectonics. */
  plateCount: number;
  /** How strongly convergent plate boundaries raise mountains, 0..1. */
  ridgeStrength: number;
  /** Amplitude of ridged mountain noise, 0..1 of the relief budget. */
  mountainStrength: number;
  /** Mountain noise wavelength, metres. */
  mountainScale: number;
  /** Domain-warp displacement as a fraction of `continentScale`. */
  warpStrength: number;
  /** Fractional detail amplitude at ground scale, 0..1. */
  detailStrength: number;
  /** Detail wavelength, metres. */
  detailScale: number;
  /** Erosion approximation strength, 0..1. Flattens slopes and cuts valleys. */
  erosion: number;
  /** Crater field density, 0..1. Zero for bodies with thick atmospheres. */
  craterDensity: number;
  /** Largest crater radius, metres. */
  craterMaxRadius: number;
  /** Dune field amplitude, metres. Zero disables dunes. */
  duneStrength: number;
  /** Dune wavelength, metres. */
  duneScale: number;
  /** Lava channel / fissure strength, 0..1. */
  fissureStrength: number;
  /** Overall roughness multiplier applied to high-frequency terms. */
  roughness: number;
}

/**
 * The elevation ramp a planet's surface colours are keyed to. Split out from
 * `SurfacePalette` because it is exactly the part the chunk mesher needs — it
 * shades vertices and knows nothing about night emission — and the mesher used
 * to declare its own identical copy of these seven fields under the name
 * `PaletteColors`, with a third hand-written list in `PlanetSurface` copying
 * one into the other field by field.
 */
export interface PaletteColors {
  deepWater: [number, number, number];
  shallowWater: [number, number, number];
  shore: [number, number, number];
  lowland: [number, number, number];
  midland: [number, number, number];
  highland: [number, number, number];
  peak: [number, number, number];
}

export interface SurfacePalette extends PaletteColors {
  /** Colour seen from orbit when the surface is in shadow (city lights, lava). */
  nightEmissive: [number, number, number];
  /** Strength of the night emissive term, 0..1. */
  nightEmissiveStrength: number;
}

export interface CelestialBodyDef {
  id: string;
  name: string;
  kind: BodyKind;
  /** Parent body id; null for the star. */
  parentId: string | null;
  /** Mean radius, metres. */
  radius: number;
  /** Surface gravity at the datum, m/s^2. Mass is derived from this. */
  surfaceGravity: number;
  /** Sidereal rotation period, seconds. Negative for retrograde rotation. */
  rotationPeriod: number;
  /** Axial tilt relative to the orbital plane, radians. */
  axialTilt: number;
  /** Rotation angle at t = 0, radians. */
  epochSpin: number;
  /** Keplerian elements relative to the parent, or null for the root star. */
  orbit: Omit<OrbitalElements, 'mu'> | null;
  surfaceClass: SurfaceClass;
  /** Whether the player can land and walk here. */
  explorable: boolean;
  atmosphere: AtmosphereDef | null;
  thermal: ThermalDef;
  hazards: HazardDef;
  terrain: TerrainProfile | null;
  palette: SurfacePalette;
  /** Biome ids available on this body, in priority order. */
  biomes: string[];
  /** 0..1; drives how many settlements are seeded. */
  settlementDensity: number;
  /** 0..1; drives whether the ecosystem generator runs at all. */
  lifePotential: number;
  /** Ring system, if any. */
  rings?: { innerRadius: number; outerRadius: number; color: [number, number, number]; opacity: number };
  /** Short flavour text used by the survey and codex UI. */
  description: string;
  /** Colour used for map markers and orbit lines. */
  markerColor: [number, number, number];
}

/** Star-specific extras layered on top of the common body definition. */
export interface StarDef extends CelestialBodyDef {
  kind: 'star';
  /** Effective surface temperature, kelvin. Drives the emitted spectrum. */
  temperature: number;
  /** Total radiated power, watts. */
  luminosity: number;
  /** Linear display colour. */
  color: [number, number, number];
}

export function isStarDef(def: CelestialBodyDef): def is StarDef {
  return def.kind === 'star';
}

/** Vacuum atmosphere used for airless bodies so callers never branch on null. */
export const VACUUM: AtmosphereDef = {
  surfaceDensity: 0,
  scaleHeight: 1,
  surfacePressure: 0,
  topAltitude: 0,
  molarMass: 0.029,
  composition: {},
  breathable: false,
  rayleigh: [0, 0, 0],
  mie: 0,
  mieAnisotropy: 0,
  hazeColor: [0, 0, 0],
  windSpeed: 0,
  turbulence: 0,
  cloudCoverage: 0,
  cloudAltitude: 0,
  cloudColor: [1, 1, 1],
};
